import React, { useState, useEffect } from "react";
import Layout from '../pages/Layout';
import Advertise from "../pages/Advertise";

const EmployeeProductivity = () => {
  // State variables for Productivity calculation
  const [outputProduced, setOutputProduced] = useState(0); // Output produced
  const [inputHoursWorked, setInputHoursWorked] = useState(0); // Input hours worked
  const [productivityResult, setProductivityResult] = useState(0); // Calculated productivity

  // Event handlers to update state values
  const handleOutputProducedChange = (e) => setOutputProduced(parseFloat(e.target.value) || 0);
  const handleInputHoursWorkedChange = (e) => setInputHoursWorked(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate productivity when any input changes
  useEffect(() => {
    if (inputHoursWorked > 0) {
      const calculatedProductivity = outputProduced / inputHoursWorked; // Calculate productivity
      setProductivityResult(calculatedProductivity);
    } else {
      setProductivityResult(0);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });

  }, [outputProduced, inputHoursWorked]);

  return (
    <Layout title="Employee Productivity Calculator" path="employee-productivity-calculator">
      <section className="section-padding" id="productivity-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Productivity Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Employee Productivity Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Output Produced Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="outputProduced" className="form-label">
                        Output Produced
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Output Produced:</strong> Enter the total output produced by the employee during the specified time period."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="outputProduced"
                        placeholder="Enter output produced"
                        value={outputProduced || ""}
                        onChange={handleOutputProducedChange}
                      />
                    </div>

                    {/* Hours Worked Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="inputHoursWorked" className="form-label">
                        Input (Hours Worked)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Input (Hours Worked):</strong> Enter the total number of hours worked by the employee during the specified time period."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="inputHoursWorked"
                        placeholder="Enter input hours worked"
                        value={inputHoursWorked || ""}
                        onChange={handleInputHoursWorkedChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Productivity:{" "}
                        <span className="fw-bold ms-3">
                          {productivityResult === 0 ? "0.00" : productivityResult.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Employee Productivity Calculator
                </h3>

                {/* What is an Employee Productivity Calculator? */}
                <h4 className="mt-3 mb-3">What is an Employee Productivity Calculator?</h4>
                <p>
                  The **Employee Productivity Calculator** helps businesses and managers measure the efficiency of their employees by calculating output relative to the input (e.g., hours worked). It is a useful tool for assessing employee and team performance in the workplace.
                </p>

                {/* Why Use an Employee Productivity Calculator? */}
                <h4 className="mt-4 mb-3">Why Use an Employee Productivity Calculator?</h4>
                <p>
                  This calculator allows businesses to track employee performance, identify areas for improvement, and make informed HR decisions. It provides valuable insights into how well employees are utilizing their time to produce results.
                </p>

                {/* Benefits of Using an Employee Productivity Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using an Employee Productivity Calculator</h4>
                <ul>
                  <li><strong>Helps managers assess individual and team performance:</strong> By tracking productivity, managers can identify top performers and areas that need improvement.</li>
                  <li><strong>Identifies areas for process improvement:</strong> If productivity is low, businesses can investigate whether processes or training need to be adjusted.</li>
                  <li><strong>Useful for HR planning and workforce development:</strong> HR departments can use the data to allocate resources more efficiently and plan for employee development.</li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use an Employee Productivity Calculator?</h4>
                <p>
                  The Employee Productivity Calculator is suitable for:
                </p>
                <ul>
                  <li><strong>HR departments:</strong> To monitor and improve employee performance.</li>
                  <li><strong>Team leaders:</strong> For tracking individual and team productivity to improve output.</li>
                  <li><strong>Corporate management:</strong> To make informed decisions about workforce allocation and resource management.</li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">How Can Low Employee Productivity Be Improved?</h4>
                <p>
                  Low productivity can be addressed by improving employee engagement, providing additional training, or streamlining processes to reduce inefficiencies. Regular feedback and setting clear expectations also help boost performance.
                </p>

                <h4 className="mt-4 mb-3">How Often Should Productivity Be Measured?</h4>
                <p>
                  Productivity should be measured regularly, but the frequency depends on the business goals. For daily tasks, productivity can be tracked more often, while for long-term projects, measuring productivity monthly or quarterly might be more appropriate.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default EmployeeProductivity;
